import { useMemo } from 'react';

export const atmosphereColors = [
    { radial: 'from-violet-500/10', blob: 'bg-violet-600/10' },
    { radial: 'from-fuchsia-500/10', blob: 'bg-fuchsia-600/10' },
    { radial: 'from-cyan-500/10', blob: 'bg-cyan-600/10' },
    { radial: 'from-blue-500/10', blob: 'bg-blue-600/10' },
    { radial: 'from-amber-500/10', blob: 'bg-amber-600/10' },
    { radial: 'from-orange-500/10', blob: 'bg-orange-600/10' },
    { radial: 'from-rose-500/10', blob: 'bg-rose-600/10' },
    { radial: 'from-emerald-500/10', blob: 'bg-emerald-600/10' },
    { radial: 'from-teal-500/10', blob: 'bg-teal-600/10' },
    { radial: 'from-indigo-500/10', blob: 'bg-indigo-600/10' },
] as const;

const driftAnimations = [
    'atmosphere-drift-1',
    'atmosphere-drift-2',
    'atmosphere-drift-3',
    'atmosphere-drift-4',
] as const;

const blobAnchors = [
    { top: '8%', right: '-8rem' },
    { top: '14%', left: '-7rem' },
    { bottom: '12%', right: '-6rem' },
    { bottom: '20%', left: '-8rem' },
    { top: '42%', right: '-10rem' },
    { top: '58%', left: '-9rem' },
    { top: '28%', right: '5%' },
    { bottom: '32%', left: '8%' },
] as const;

const blobSizes = ['size-96', 'size-80', 'size-72'] as const;

export type AtmosphereBlob = {
    color: string;
    anchor: {
        top?: string;
        right?: string;
        bottom?: string;
        left?: string;
    };
    animationName: (typeof driftAnimations)[number];
    duration: string;
    delay: string;
    size: (typeof blobSizes)[number];
};

function shuffle<T>(items: readonly T[]): T[] {
    const copy = [...items];

    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }

    return copy;
}

function pickDriftDuration(): string {
    return `${20 + Math.floor(Math.random() * 16)}s`;
}

function pickDriftDelay(): string {
    return `${Math.floor(Math.random() * 10)}s`;
}

function createBlob(
    color: string,
    anchor: (typeof blobAnchors)[number],
    animationName: (typeof driftAnimations)[number],
    size: (typeof blobSizes)[number],
): AtmosphereBlob {
    return {
        color,
        anchor,
        animationName,
        duration: pickDriftDuration(),
        delay: pickDriftDelay(),
        size,
    };
}

export function useAtmosphereAmbience(): {
    radial: string;
    blobs: AtmosphereBlob[];
} {
    return useMemo(() => {
        const colors = shuffle(atmosphereColors);
        const anchors = shuffle(blobAnchors);
        const animations = shuffle(driftAnimations);
        const sizes = shuffle(blobSizes);

        return {
            radial: colors[0].radial,
            blobs: [
                createBlob(
                    colors[1].blob,
                    anchors[0],
                    animations[0],
                    sizes[0],
                ),
                createBlob(
                    colors[2].blob,
                    anchors[1],
                    animations[1] ?? animations[0],
                    sizes[1] ?? sizes[0],
                ),
            ],
        };
    }, []);
}
